import type { TransitionStatus } from 'react-transition-group';
import styled, { css } from 'styled-components';

import { focusStyleDark, tabletLandscape } from '@/components/utils';
import ArrowDown from '@/icons/small/Arrow-Thin-Down.svg';

const shimTransitionStyles = {
  entering: null,
  entered: css`
    opacity: 1;
  `,
  exiting: css`
    opacity: 0;
  `,
  exited: css`
    opacity: 0;
  `,
  unmounted: null,
};

const listTransitionStyles = {
  entering: null,
  entered: css`
    transform: translate3d(0, 0, 0);
  `,
  exiting: css`
    transform: translate3d(0, 100vh, 0);
  `,
  exited: css`
    transform: translate3d(0, 100vh, 0);
  `,
  unmounted: null,
};

export const StyledSelectValue = styled.button<{ $isOpen: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border: var(--distance-static-3xs) solid transparent;
  padding: var(--distance-dynamic-xxs) var(--distance-static-s);
  background-color: transparent;
  color: currentColor;

  &::before {
    content: '';
    position: absolute;
    right: 0;
    height: var(--distance-static-xl);
    width: 1px;
    background: var(--color-searchinput-divider);
  }

  ${focusStyleDark};

  &:focus-visible {
    &::after {
      top: -3px;
      right: 0;
      bottom: -3px;
      left: -3px;
      border-radius: var(--border-radius-s);
    }
  }

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: -3px;
        right: 0;
        bottom: -3px;
        left: -3px;
        border: var(--distance-static-3xs) solid var(--color-focus-dark);
        border-radius: var(--border-radius-s);
      }
    `}
`;

export const StyledSelectList = styled.ul<{ $state?: TransitionStatus }>`
  position: fixed;
  z-index: var(--zindex-l);
  left: 0;
  right: 0;
  bottom: 0;
  padding: var(--distance-static-m);
  margin: 0;
  list-style: none;
  background-color: var(--color-surface-light);
  border-radius: var(--border-radius-l) var(--border-radius-l) 0 0;
  transition: transform 0.3s ease-in-out;

  ${({ $state }) => $state && listTransitionStyles[$state]};

  @media screen and (${tabletLandscape}) {
    position: absolute;
    top: calc(100% + var(--distance-static-3xs));
    bottom: unset;
    width: 0;
    min-width: 248px;
    padding: 0;
    box-shadow: var(--shadow-m);
    border-radius: var(--border-radius-s);
    transition: none;
  }
`;

export const StyledSelectListItem = styled.li<{
  $isActive: boolean;
  $isSelected: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: var(--distance-static-xs) var(--distance-static-s);

  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: var(--color-searchinput-result-hover);
    `}
`;

export const StyledShim = styled.div<{ $state?: TransitionStatus }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: var(--zindex-l);
  background: var(--color-overlay-background);
  transition: opacity 0.2s ease-in-out;

  ${({ $state }) => $state && shimTransitionStyles[$state]};

  @media screen and (${tabletLandscape}) {
    display: none;
  }
`;

export const StyledArrow = styled(ArrowDown)<{ $isOpen: boolean }>`
  transition: transform 0.2s ease-in-out;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

export const StyledOptionIcon = styled.span`
  margin-right: var(--distance-static-s);
`;

export const StyledListItemContent = styled.div`
  display: flex;
  align-items: center;
`;
